import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Upload, Loader2 } from "lucide-react";
import { resolveImg, onImgError } from "@/lib/images";

const empty = { name: "", type: "camera", short_description: "", description: "", max_guests: 2, beds: 1, baths: 1, price_per_night: 60, amenities: [], images: [], address: "", active: true };

export default function AdminProperties() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => http.get("/properties?active_only=false").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const open = (p) => {
    if (p) { setEditing(p.property_id); setForm({ ...p, amenities: p.amenities || [], images: p.images || [] }); }
    else { setEditing(null); setForm(empty); }
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      max_guests: Number(form.max_guests), beds: Number(form.beds), baths: Number(form.baths),
      price_per_night: Number(form.price_per_night),
      amenities: typeof form.amenities === "string" ? form.amenities.split(",").map(s => s.trim()).filter(Boolean) : form.amenities,
      images: Array.isArray(form.images) ? form.images : String(form.images).split("\n").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await http.put(`/properties/${editing}`, payload);
      else await http.post("/properties", payload);
      toast.success("Salvato");
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const del = async (id) => {
    if (!confirm("Eliminare questa proprietà?")) return;
    await http.delete(`/properties/${id}`);
    toast.success("Eliminata");
    load();
  };

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await http.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || "upload fallito"}`);
      }
    }
    if (uploaded.length) {
      setForm((f) => ({ ...f, images: [...(Array.isArray(f.images) ? f.images : []), ...uploaded] }));
      toast.success(`${uploaded.length} immagine/i caricate`);
    }
    setUploading(false);
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: (Array.isArray(f.images) ? f.images : []).filter((_, i) => i !== idx) }));
  };

  return (
    <AdminLayout>
      <div className="p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="eyebrow mb-2">Gestione</div>
            <h1 className="font-serif text-4xl">Proprietà</h1>
          </div>
          <button className="mel-btn-primary" onClick={() => open(null)} data-testid="new-property-btn">
            <Plus size={16} /> Nuova proprietà
          </button>
        </div>

        <div className="border border-[var(--mel-border)] rounded-md overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="properties-table">
            <thead className="bg-[var(--mel-surface)] text-left">
              <tr>
                <th className="px-4 py-3 w-16">Foto</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Ospiti</th>
                <th className="px-4 py-3">Prezzo</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.property_id} className="border-t border-[var(--mel-border)]" data-testid={`property-row-${p.property_id}`}>
                  <td className="px-4 py-3"><img src={resolveImg(p.images?.[0])} onError={onImgError} alt="" className="w-12 h-12 object-cover rounded-md" /></td>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 capitalize">{p.type}</td>
                  <td className="px-4 py-3">{p.max_guests}</td>
                  <td className="px-4 py-3">€{p.price_per_night}</td>
                  <td className="px-4 py-3">{p.active ? <span className="text-[var(--mel-accent)]">Attiva</span> : <span className="text-[var(--mel-muted)]">Nascosta</span>}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button onClick={() => open(p)} className="p-2 hover:text-[var(--mel-primary)]" data-testid={`edit-${p.property_id}`}><Edit3 size={16} /></button>
                    <button onClick={() => del(p.property_id)} className="p-2 hover:text-red-600" data-testid={`del-${p.property_id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="property-modal">
          <div className="bg-white rounded-md max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[var(--mel-border)]">
              <div className="font-serif text-xl">{editing ? "Modifica proprietà" : "Nuova proprietà"}</div>
              <button onClick={() => setShowModal(false)} data-testid="close-modal"><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 grid grid-cols-2 gap-4">
              <label className="col-span-2"><div className="text-xs eyebrow">Nome</div><input required className="w-full mt-1 border rounded-md px-3 py-2 text-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" /></label>
              <label><div className="text-xs eyebrow">Tipo</div>
                <select className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="pf-type">
                  <option value="camera">Camera</option><option value="loft">Loft</option>
                </select>
              </label>
              <label><div className="text-xs eyebrow">Prezzo / notte (€)</div><input type="number" required className="w-full mt-1 border rounded-md px-3 py-2 text-sm" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} data-testid="pf-price" /></label>
              <label><div className="text-xs eyebrow">Ospiti max</div><input type="number" required className="w-full mt-1 border rounded-md px-3 py-2 text-sm" value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: e.target.value })} data-testid="pf-guests" /></label>
              <label><div className="text-xs eyebrow">Letti</div><input type="number" className="w-full mt-1 border rounded-md px-3 py-2 text-sm" value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })} /></label>
              <label><div className="text-xs eyebrow">Bagni</div><input type="number" className="w-full mt-1 border rounded-md px-3 py-2 text-sm" value={form.baths} onChange={(e) => setForm({ ...form, baths: e.target.value })} /></label>
              <label className="col-span-2"><div className="text-xs eyebrow">Descrizione breve</div><input className="w-full mt-1 border rounded-md px-3 py-2 text-sm" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></label>
              <label className="col-span-2"><div className="text-xs eyebrow">Descrizione</div><textarea rows="4" className="w-full mt-1 border rounded-md px-3 py-2 text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
              <label className="col-span-2"><div className="text-xs eyebrow">Servizi (separati da virgola)</div><input className="w-full mt-1 border rounded-md px-3 py-2 text-sm" value={Array.isArray(form.amenities) ? form.amenities.join(", ") : form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} /></label>

              {/* Image manager */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs eyebrow">Foto della proprietà</div>
                  <label className="mel-btn-outline text-xs cursor-pointer" data-testid="upload-images-btn">
                    {uploading ? <><Loader2 size={14} className="animate-spin" /> Caricamento…</> : <><Upload size={14} /> Carica foto</>}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={(e) => handleUpload(e.target.files)} disabled={uploading} data-testid="upload-images-input" />
                  </label>
                </div>
                <div className="grid grid-cols-4 gap-3" data-testid="image-gallery">
                  {(Array.isArray(form.images) ? form.images : []).map((src, i) => (
                    <div key={src} className="relative group aspect-square rounded-md overflow-hidden border border-[var(--mel-border)]" data-testid={`img-${i}`}>
                      <img src={resolveImg(src)} onError={onImgError} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded md:opacity-0 md:group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-white transition-opacity" aria-label="Rimuovi foto" data-testid={`remove-img-${i}`}>
                        <X size={12} />
                      </button>
                      {i === 0 && <div className="absolute bottom-1 left-1 bg-[var(--mel-primary)] text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded">Copertina</div>}
                    </div>
                  ))}
                  {(!form.images || form.images.length === 0) && (
                    <div className="col-span-4 border border-dashed border-[var(--mel-border)] rounded-md p-8 text-center text-xs text-[var(--mel-muted)]">
                      Nessuna foto. Clicca "Carica foto" per aggiungere immagini reali.
                    </div>
                  )}
                </div>
              </div>

              <label className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Visibile sul sito pubblico</label>
              <div className="col-span-2 flex gap-3 mt-4">
                <button className="mel-btn-primary" data-testid="save-property">Salva</button>
                <button type="button" onClick={() => setShowModal(false)} className="mel-btn-outline">Annulla</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
